import React, { useState } from 'react';
import { api } from '../utils/api';

/**
 * TermsReacknowledgeBanner
 *
 * Displays a banner on the parent dashboard when the organisation admin
 * has materially updated the Terms & Conditions document since this
 * parent last accepted them. The parent can re-confirm in one click,
 * which calls POST /api/users/me/reacknowledge-terms to bump their
 * `terms_acknowledged_version`.
 *
 * Driven by the `terms_status` block included on /api/auth/me:
 *   {
 *     current_version, acknowledged_version,
 *     needs_reacknowledge, terms_url
 *   }
 *
 * Self-contained: only renders when needs_reacknowledge is true.
 */
export function TermsReacknowledgeBanner({ user, organization, onAcknowledged }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const status = user && user.terms_status;
  if (!status || !status.needs_reacknowledge) return null;

  const handleAck = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.reacknowledgeTerms();
      if (onAcknowledged) onAcknowledged();
    } catch (err) {
      setError(err.message || 'Could not record acknowledgement');
      setBusy(false);
    }
  };

  return (
    <div
      className="card"
      data-testid="terms-reack-banner"
      style={{
        marginBottom: '1.25rem',
        borderColor: 'hsl(220 80% 70%)',
        background: 'hsl(220 80% 97%)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: '14rem' }}>
          <h3 style={{ margin: 0 }}>📜 Updated Terms &amp; Conditions</h3>
          <p style={{ margin: '0.4rem 0 0', fontSize: '0.9rem', color: 'hsl(var(--muted-foreground))' }}>
            <strong>{organization?.name || 'Your provider'}</strong> has published version{' '}
            <strong>{status.current_version}</strong> of the Terms &amp; Conditions
            (you previously accepted v{status.acknowledged_version || 0}).
            Please review and confirm to keep using the service.
          </p>
          {status.terms_url && (
            <p style={{ margin: '0.5rem 0 0', fontSize: '0.85rem' }}>
              <a
                href={status.terms_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="terms-reack-external-link"
              >
                Open the full document in a new tab ↗
              </a>
            </p>
          )}
          {error && (
            <p style={{ margin: '0.5rem 0 0', fontSize: '0.85rem', color: 'hsl(var(--destructive))' }} data-testid="terms-reack-error">
              {error}
            </p>
          )}
        </div>
        <button
          type="button"
          className="btn btn-primary"
          disabled={busy}
          onClick={handleAck}
          data-testid="terms-reack-confirm"
          style={{ flex: '0 0 auto' }}
        >
          {busy ? 'Saving…' : `I accept v${status.current_version}`}
        </button>
      </div>
    </div>
  );
}

export default TermsReacknowledgeBanner;
